import {
  useCallback,
  useEffect,
  useMemo,
  useState,
  type AnchorHTMLAttributes,
  type MouseEvent,
  type PropsWithChildren,
} from "react";
import { NavigationContext, useNavigation } from "./router-context";

export function RouterProvider({ children }: PropsWithChildren) {
  const [path, setPath] = useState(window.location.pathname);

  useEffect(() => {
    const updatePath = () => setPath(window.location.pathname);
    window.addEventListener("popstate", updatePath);
    return () => window.removeEventListener("popstate", updatePath);
  }, []);

  const navigate = useCallback(
    (nextPath: string, options?: { replace?: boolean }) => {
      if (options?.replace) {
        window.history.replaceState({}, "", nextPath);
      } else {
        window.history.pushState({}, "", nextPath);
      }
      setPath(window.location.pathname);
      window.scrollTo({ top: 0, behavior: "smooth" });
    },
    [],
  );

  const value = useMemo(() => ({ path, navigate }), [path, navigate]);
  return (
    <NavigationContext.Provider value={value}>
      {children}
    </NavigationContext.Provider>
  );
}

export function AppLink({
  href,
  children,
  className,
  ...props
}: AnchorHTMLAttributes<HTMLAnchorElement> & { href: string }) {
  const { path, navigate } = useNavigation();
  const active = path === href;
  const follow = (event: MouseEvent<HTMLAnchorElement>) => {
    props.onClick?.(event);
    if (
      event.defaultPrevented ||
      event.button !== 0 ||
      event.metaKey ||
      event.ctrlKey ||
      event.shiftKey ||
      event.altKey
    ) {
      return;
    }
    event.preventDefault();
    navigate(href);
  };
  return (
    <a
      {...props}
      href={href}
      className={className}
      aria-current={active ? "page" : props["aria-current"]}
      onClick={follow}
    >
      {children}
    </a>
  );
}
